import {
  type ManagedCustomer,
  type QuestionnaireAnswer,
  isFullNewCustomerApplication,
} from './business-workflow'

export type CustomerDuplicateMatchField = 'companyName' | 'taxId' | 'erpCustomerCode'

export type CustomerDuplicateCandidate = {
  customer: ManagedCustomer
  matchedFields: CustomerDuplicateMatchField[]
}

type ManagedCustomerDuplicateComparisonField = keyof Pick<
  ManagedCustomer,
  | 'companyName'
  | 'taxId'
  | 'industry'
  | 'customerType'
  | 'departmentName'
  | 'companySize'
  | 'companyAddress'
  | 'companyPhone'
  | 'contactName'
  | 'contactEmail'
  | 'contactPhone'
  | 'salesRegion'
>

type AnswerOnlyCustomerDuplicateComparisonField =
  | 'customerSource'
  | 'responsiblePerson'
  | 'companyFullName'
  | 'taxIdMissingReason'
  | 'foodBusinessRegNo'
  | 'fax'
  | 'invoiceAddress'
  | 'registeredAddress'
  | 'billingEmail'
  | 'deliveryTimeNote'
  | 'businessHours'
  | 'salesRep'
  | 'customerCategory'
  | 'remark'
  | 'submittedBy'

export type CustomerDuplicateComparisonField =
  | ManagedCustomerDuplicateComparisonField
  | AnswerOnlyCustomerDuplicateComparisonField

export type CustomerDuplicateComparisonRow = {
  field: CustomerDuplicateComparisonField
  label: string
  customerValue: string
  comparedValue: string
  status: 'same' | 'different'
  statusLabel: '相同' | '不同'
  selectable: boolean
}

type DuplicateComparisonFieldConfig = {
  field: CustomerDuplicateComparisonField
  label: string
  getValue: (customer: ManagedCustomer) => unknown
  patchField?: ManagedCustomerDuplicateComparisonField
  answerPatch?: {
    mappedField: string
    questionTitle: string
    questionType: string
    mappedFields: string[]
    questionTitles: string[]
  }
}

const duplicateComparisonFields: DuplicateComparisonFieldConfig[] = [
  answerField('customerSource', '新客戶來源', ['customerSource'], ['新客戶來源']),
  managedField('companyName', '客戶簡稱'),
  answerField('responsiblePerson', '負責人', ['ownerName'], ['負責人'], 'ownerName'),
  managedField('contactName', '聯絡人'),
  managedField('companyPhone', '市話'),
  managedField('contactPhone', '手機'),
  answerField('fax', '傳真號碼', ['fax'], ['傳真號碼']),
  managedField('contactEmail', 'E-Mail'),
  managedField('taxId', '統一編號'),
  answerField('companyFullName', '統編抬頭（公司全名）', ['companyFullName'], ['統編抬頭（公司全名）']),
  answerField('taxIdMissingReason', '沒有統編的原因', ['taxIdMissingReason', 'noTaxIdReason', 'no_tax_id_reason'], ['沒有統編的原因']),
  managedField('companySize', '員工人數'),
  managedField('companyAddress', '送貨地址'),
  answerField('invoiceAddress', '發票地址', ['invoiceAddress'], ['發票地址']),
  answerField('registeredAddress', '登記地址', ['registeredAddress'], ['登記地址']),
  answerField('billingEmail', '帳單收件Mail', ['billingEmail'], ['帳單收件Mail']),
  answerField('deliveryTimeNote', '可收貨時間', ['deliveryTimeNote'], ['可收貨時間']),
  answerField('businessHours', '營業時段、公休日', ['businessHours'], ['營業時段、公休日']),
  managedField('departmentName', '部門別'),
  answerField(
    'salesRep',
    '業務',
    ['salesRepSpecial', 'salesRepDiv1', 'salesRepDiv2', 'salesRepDiv3', 'salesRepSpecialCh', 'salesRepDiv4'],
    ['特販業務', '營一業務', '營二業務', '營三業務', '特通業務', '營四業務'],
    'salesRepSpecial',
  ),
  answerField('customerCategory', '客戶類別', ['customerCategory'], ['客戶類別']),
  managedField('industry', '客戶通路別'),
  managedField('customerType', '型態別'),
  managedField('salesRegion', '客戶區域別'),
  answerField('remark', '備註', ['remark'], ['備註']),
  answerField('submittedBy', '填單人員', ['submittedBy'], ['填單人員']),
  answerField('foodBusinessRegNo', '食品業者登入字號', ['foodBusinessRegNo'], ['食品業者登入字號']),
]

const fullNewComparisonFields: CustomerDuplicateComparisonField[] = [
  'customerSource',
  'companyName',
  'responsiblePerson',
  'contactName',
  'companyPhone',
  'contactPhone',
  'fax',
  'contactEmail',
  'taxId',
  'companyFullName',
  'taxIdMissingReason',
  'companySize',
  'companyAddress',
  'invoiceAddress',
  'registeredAddress',
  'billingEmail',
  'deliveryTimeNote',
  'businessHours',
  'departmentName',
  'salesRep',
  'customerCategory',
  'industry',
  'customerType',
  'salesRegion',
  'remark',
  'submittedBy',
  'foodBusinessRegNo',
]

const existingChangeComparisonFieldsByType: Record<string, CustomerDuplicateComparisonField[]> = {
  既有客戶變更電話: ['companyName', 'companyPhone', 'contactPhone'],
  既有客戶變更統編: [
    'companyName',
    'taxId',
    'companyFullName',
    'taxIdMissingReason',
    'foodBusinessRegNo',
  ],
  既有客戶變更送貨地址: ['companyName', 'companyAddress'],
  既有客戶變更送貨時段: ['companyName', 'deliveryTimeNote', 'businessHours'],
}

export function createCustomerDuplicateCandidates(
  currentCustomer: ManagedCustomer,
  customers: ManagedCustomer[],
): CustomerDuplicateCandidate[] {
  if (!isFullNewCustomerApplication(currentCustomer)) return []

  const currentName = normalizeComparable(currentCustomer.companyName)
  const currentTaxId = normalizeComparable(currentCustomer.taxId)
  const currentCustomerCode = normalizeComparable(currentCustomer.erpCustomerCode)

  return customers
    .filter((customer) => customer.id !== currentCustomer.id)
    .filter((customer) => customer.status !== 'archived')
    .map((customer) => {
      const matchedFields: CustomerDuplicateMatchField[] = []
      if (currentName && normalizeComparable(customer.companyName) === currentName) {
        matchedFields.push('companyName')
      }
      if (currentTaxId && normalizeComparable(customer.taxId) === currentTaxId) {
        matchedFields.push('taxId')
      }
      if (
        currentCustomerCode &&
        normalizeComparable(customer.erpCustomerCode) === currentCustomerCode
      ) {
        matchedFields.push('erpCustomerCode')
      }
      return { customer, matchedFields }
    })
    .filter((candidate) => candidate.matchedFields.length > 0)
}

export function createCustomerDuplicateComparisonRows(
  customer: ManagedCustomer,
  comparedCustomer: ManagedCustomer,
): CustomerDuplicateComparisonRow[] {
  const allowedFields = new Set(getComparisonFieldsForComparedCustomer(comparedCustomer))
  return duplicateComparisonFields.filter((config) => allowedFields.has(config.field)).map((config) => {
    const customerValue = formatComparisonValue(config.field, config.getValue(customer))
    const comparedValue = formatComparisonValue(config.field, config.getValue(comparedCustomer))
    const status = normalizeComparable(customerValue) === normalizeComparable(comparedValue)
      ? 'same'
      : 'different'
    return {
      field: config.field,
      label: config.label,
      customerValue,
      comparedValue,
      status,
      statusLabel: status === 'same' ? '相同' : '不同',
      selectable: Boolean(config.patchField || config.answerPatch) && comparedValue.trim() !== '',
    }
  })
}

export function buildCustomerDuplicateFieldPatch(
  customer: ManagedCustomer,
  comparedCustomer: ManagedCustomer,
  selectedFields: CustomerDuplicateComparisonField[],
): Partial<ManagedCustomer> {
  const patch: Partial<ManagedCustomer> = {}
  let patchedAnswers: QuestionnaireAnswer[] | null = null
  for (const field of selectedFields) {
    const config = duplicateComparisonFields.find((item) => item.field === field)
    if (!config) continue
    if (config.patchField) {
      ;(patch as Record<string, unknown>)[config.patchField] = comparedCustomer[config.patchField]
      continue
    }
    if (!config.answerPatch) continue
    const comparedAnswer = findQuestionnaireAnswer(
      comparedCustomer,
      config.answerPatch.mappedFields,
      config.answerPatch.questionTitles,
    )
    const comparedValue = formatComparableValue(comparedAnswer?.answer ?? config.getValue(comparedCustomer))
    if (!comparedValue) continue
    patchedAnswers ??= [...customer.answers]
    const currentIndex = patchedAnswers.findIndex(
      (answer) =>
        config.answerPatch &&
        answerMatches(answer, config.answerPatch.mappedFields, config.answerPatch.questionTitles),
    )
    if (currentIndex >= 0) {
      patchedAnswers[currentIndex] = {
        ...patchedAnswers[currentIndex],
        answer: comparedValue,
        mappedField: patchedAnswers[currentIndex].mappedField || config.answerPatch.mappedField,
        questionTitle: patchedAnswers[currentIndex].questionTitle || config.answerPatch.questionTitle,
        questionType: patchedAnswers[currentIndex].questionType || config.answerPatch.questionType,
      }
      continue
    }
    patchedAnswers.push({
      id: `duplicate-compare-${config.answerPatch.mappedField}`,
      questionTitle: config.answerPatch.questionTitle,
      questionType: config.answerPatch.questionType,
      answer: comparedValue,
      mappedField: config.answerPatch.mappedField,
    })
  }
  if (patchedAnswers) patch.answers = patchedAnswers
  return patch
}

export function getCustomerDuplicateMatchLabel(field: CustomerDuplicateMatchField) {
  if (field === 'companyName') return '客戶簡稱相同'
  if (field === 'taxId') return '統一編號相同'
  return '既有客戶代號相同'
}

function normalizeComparable(value: unknown) {
  return String(value ?? '')
    .trim()
    .toLocaleLowerCase()
    .replace(/\s+/g, '')
}

function formatComparableValue(value: unknown) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return Number.isFinite(value) && value !== 0 ? String(value) : ''
  return String(value).trim()
}

function managedField(
  field: ManagedCustomerDuplicateComparisonField,
  label: string,
): DuplicateComparisonFieldConfig {
  return {
    field,
    label,
    patchField: field,
    getValue: (customer) => customer[field],
  }
}

function answerField(
  field: AnswerOnlyCustomerDuplicateComparisonField,
  label: string,
  mappedFields: string[],
  questionTitles: string[],
  mappedField: string = field,
): DuplicateComparisonFieldConfig {
  return {
    field,
    label,
    answerPatch: {
      mappedField,
      questionTitle: label,
      questionType: 'SHORT_TEXT',
      mappedFields,
      questionTitles,
    },
    getValue: (customer) => findQuestionnaireAnswer(customer, mappedFields, questionTitles)?.answer,
  }
}

function getComparisonFieldsForComparedCustomer(
  comparedCustomer: ManagedCustomer,
): CustomerDuplicateComparisonField[] {
  const formType = findQuestionnaireAnswer(comparedCustomer, ['formType'], ['新客戶建檔選擇'])
    ?.answer
  if (normalizeFlowAnswer(formType) === '全新客戶') {
    return fullNewComparisonFields
  }
  const changeType = findQuestionnaireAnswer(comparedCustomer, ['changeType'], ['資料變更選擇'])
    ?.answer
  return (
    existingChangeComparisonFieldsByType[normalizeFlowAnswer(changeType)] ??
    fullNewComparisonFields
  )
}

function findQuestionnaireAnswer(
  customer: ManagedCustomer,
  mappedFields: string[],
  questionTitles: string[],
) {
  return (
    customer.answers.find(
      (answer) =>
        answerMatches(answer, mappedFields, questionTitles) &&
        formatComparableValue(answer.answer).trim() !== '',
    ) ?? customer.answers.find((answer) => answerMatches(answer, mappedFields, questionTitles))
  )
}

function answerMatches(answer: QuestionnaireAnswer, mappedFields: string[], questionTitles: string[]) {
  const mappedField = normalizeFlowAnswer(answer.mappedField)
  const questionTitle = normalizeFlowAnswer(answer.questionTitle)
  return (
    mappedFields.some((field) => normalizeFlowAnswer(field) === mappedField) ||
    questionTitles.some((title) => normalizeFlowAnswer(title) === questionTitle)
  )
}

function normalizeFlowAnswer(value: unknown) {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, '')
}

function formatComparisonValue(field: CustomerDuplicateComparisonField, value: unknown) {
  return formatComparableValue(value)
}
